/**
 * ReactionChips — the reactions on a bubble (PR-4, 2026-09-09; restyled
 * 2026-09-09): one chip per emoji with its count; the viewer's own reactions
 * read as pressed; a click toggles; the accessible label names who reacted.
 * Lives at the head of the meta line (MetaLeading — Miles: "be a part of the
 * bottom line of a message"), never in its own row.
 *
 * CONTRAST (Miles, 2026-09-09, screenshot of a gold bubble with a faint
 * translucent circle and a ~10 px emoji: "need more contrast and need to be
 * colorful like they are but somehow need to be seen better"): a solid,
 * near-white pill with a hairline border and a soft shadow reads on the gold
 * user bubble AND on the soft-white Prest0n bubble; the emoji stays the
 * platform's own colourful glyph at a size you can actually see; the count is
 * bold; the viewer's own chip carries a gold outline. Alpha tints and #fff
 * pass the design-token gate.
 *
 * WHO REACTED — THE CHIP IS THE DOOR (2026-09-20, Miles: "I don't want yet
 * another element. Please do this with a touch and hold gesture in mobile view
 * or hover over"). The 2026-09-19 answer to "on a phone there's no way to see
 * it" was a trailing › pill, ALWAYS VISIBLE, sitting in this group beside the
 * chips. It worked and it was one control too many, so it is gone and the
 * gestures carry the whole job. You ask a chip about itself:
 *   • TOUCH — a press-and-hold, routed by the host ahead of the picker
 *     (GlobalChatSystem.tsx, transcriptLongPress: the chip is inside the row,
 *     so the narrower target has to be read first or it never wins);
 *   • MOUSE — the same hold (useLongPress is pointer-based, so a held left
 *     button fires it too), or a right-click;
 *   • KEYBOARD — the Menu key / Shift+F10, which the browser delivers as
 *     `contextmenu` on the focused element, so ONE handler serves the mouse
 *     and the keyboard; plus ArrowDown, because most laptops have no Menu key
 *     and ArrowDown-opens-the-popup is the ARIA convention for a control that
 *     has one. Enter and Space are left alone — they toggle.
 * On a fine pointer, hovering or focusing a chip opens the styled hover card
 * instead of the OS tooltip — instant, and truncated by the model rather than
 * by the window manager. `title` is gone: it would fire underneath the card.
 * The card names everyone it can and states the gesture when it cannot, which
 * — with the press cue below — is the whole of the discoverability budget a
 * rule against new elements leaves.
 */
import React, { useCallback, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { LONG_PRESS_HOLD_MS } from '../../hooks/useLongPress';
import type { ReactionSummaryEntry } from '../../services/messageReactions/reactionModel';
import type { PickerAnchor, PickerRect } from '../../services/messageReactions/pickerPlacement';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import { ReactionHoverCard } from './ReactionDetails';

export interface ReactionChipsProps {
  reactions: ReadonlyArray<ReactionSummaryEntry>;
  /** uid → display name for the accessible label and the hover card. */
  labelFor: (uid: string) => string;
  onToggle: (emoji: string) => void;
  /** False while the message cannot take a reaction yet (not delivered). */
  canReact?: boolean;
  /**
   * Opens the activity surface on the emoji asked about, anchored on the chip.
   * Absent ⇒ no gesture door here and no hover card (the host has nothing to open).
   */
  onShowDetails?: (anchorEl: HTMLElement, emoji?: string) => void;
  /**
   * The names for the hover card, from the shared model — so the card, the
   * sheet and the panel can never disagree about who. `truncated` is the card's
   * cue to state the gesture that reaches the rest.
   */
  previewFor?: (entry: ReactionSummaryEntry) => { text: string; truncated: boolean };
  /** The pane rect the hover card must stay inside, read at hover time. */
  boundsFor?: () => PickerRect | null;
}

export const ReactionChipsGroup = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  user-select: none;
`;

/**
 * THE PRESS CUE (2026-09-20) — how a thumb learns the hold with no new element.
 * While a chip is held, a gold ring ramps in over EXACTLY the hold duration, so
 * resting a finger a fraction too long shows the gesture starting and lifting
 * early visibly abandons it. A quick tap is a ~60ms flash that reads as ordinary
 * press feedback.
 *
 * The duration is interpolated from useLongPress's own constant and is never
 * typed here: a cue that promises 450ms while the timer waits 600ms teaches the
 * wrong thing, and the two would drift the first time anyone tuned one of them.
 */
const chipHold = keyframes`
  from { box-shadow: 0 0 0 0 rgba(198, 167, 94, 0); }
  to   { box-shadow: 0 0 0 2px var(--brand-gold); }
`;

export const ReactionChip = styled.button<{ $mine?: boolean; $canReact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px 2px 7px;
  /* The chip is the only door now, so it is also a target: comfortably past the
     24px WCAG 2.5.8 floor on a mouse, and bigger under a thumb. Not 44 — a row
     of 44px chips would dominate the bubble, and 2.5.8's spacing exception is
     already met by the 4px the group puts between them. */
  min-height: 28px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$mine ? 'var(--brand-gold-text)' : 'rgba(0, 0, 0, 0.16)')};
  box-shadow: ${(p) => (p.$mine
    ? '0 0 0 1px var(--brand-gold-text), 0 1px 3px rgba(0, 0, 0, 0.2)'
    : '0 1px 3px rgba(0, 0, 0, 0.2)')};
  background: rgba(255, 255, 255, 0.94);
  color: var(--brand-black);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  line-height: 1.25;
  cursor: ${(p) => (p.$canReact === false ? 'default' : 'pointer')};
  appearance: none;
  -webkit-appearance: none;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.24);
  }
  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.9;
  }

  ${css`
    &:active:not(:disabled) {
      animation: ${chipHold} ${LONG_PRESS_HOLD_MS}ms linear forwards;
    }
  `}

  @media (prefers-reduced-motion: reduce) {
    &:active:not(:disabled) {
      animation: none;
    }
  }

  @media (pointer: coarse) {
    min-height: 32px;
  }
`;

/** The emoji at a size you can see: the platform's own colourful glyph, never an icon font. */
const Emoji = styled.span`
  font-size: 1.05rem;
  line-height: 1;
`;

const Count = styled.span`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

/**
 * The label answers first and instructs second: every name, uncapped — so the
 * screen-reader path is the most COMPLETE path here and never needed a gesture
 * at all — and only then, when there is a surface to open, how to reach it.
 */
export function reactionChipLabel(
  entry: ReactionSummaryEntry,
  labelFor: (uid: string) => string,
  lang: 'en' | 'es',
  hasDetails = false,
): string {
  const names = entry.uids.map(labelFor).filter(Boolean);
  const who = names.join(', ');
  const base = `${entry.emoji} ${entry.count} — ${who}`;
  if (!hasDetails) return base;
  return lang === 'es'
    ? `${base} · mantén pulsado para ver los detalles`
    : `${base} · press and hold for details`;
}

const anchorOf = (el: HTMLElement): PickerAnchor => {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, width: r.width, height: r.height };
};

export function ReactionChips({
  reactions, labelFor, onToggle, canReact = true, onShowDetails, previewFor, boundsFor,
}: ReactionChipsProps): React.ReactElement | null {
  const [hover, setHover] = useState<{ emoji: string; names: string; truncated: boolean; anchor: PickerAnchor; bounds: PickerRect | null } | null>(null);

  const openHover = useCallback((entry: ReactionSummaryEntry, el: HTMLElement | null) => {
    if (!el || !previewFor) return;
    const preview = previewFor(entry);
    if (!preview?.text) return;
    setHover({
      emoji: entry.emoji,
      names: preview.text,
      truncated: preview.truncated,
      anchor: anchorOf(el),
      bounds: boundsFor?.() ?? null,
    });
  }, [previewFor, boundsFor]);
  const closeHover = useCallback(() => setHover(null), []);
  /**
   * The one opener behind every gesture: right-click, the Menu key (which the
   * browser delivers here as `contextmenu`), and ArrowDown. The touch/mouse
   * HOLD reaches the same surface through the host's delegated router, so the
   * two files name each other rather than each owning half an answer.
   */
  const askAboutChip = useCallback((entry: ReactionSummaryEntry, el: HTMLElement) => {
    if (!onShowDetails) return;
    closeHover();
    onShowDetails(el, entry.emoji);
  }, [onShowDetails, closeHover]);

  if (!reactions || reactions.length === 0) return null;
  const lang = getUserUiLanguage() === 'es' ? 'es' : 'en';
  return (
    <ReactionChipsGroup
      data-reaction-chips=""
      role="group"
      aria-label={lang === 'es' ? 'reacciones' : 'reactions'}
    >
      {reactions.map((entry) => {
        const label = reactionChipLabel(entry, labelFor, lang, !!onShowDetails);
        return (
          <ReactionChip
            key={entry.emoji}
            type="button"
            data-reaction-chip={entry.emoji}
            data-reaction-count={entry.count}
            data-reaction-mine={entry.mine ? 'true' : undefined}
            aria-pressed={entry.mine}
            aria-label={label}
            aria-haspopup={onShowDetails ? 'dialog' : undefined}
            disabled={!canReact}
            $mine={entry.mine}
            $canReact={canReact}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(entry.emoji);
            }}
            // Right-click on a mouse, Menu key / Shift+F10 on a keyboard — the
            // browser raises the same event for both, so this is one door.
            onContextMenu={(e) => {
              if (!onShowDetails) return;
              e.preventDefault();
              e.stopPropagation();
              askAboutChip(entry, e.currentTarget as HTMLElement);
            }}
            // ArrowDown, for the keyboards with no Menu key. Enter and Space are
            // deliberately untouched: they are the toggle, and the toggle is the
            // thing people came for.
            onKeyDown={(e) => {
              if (!onShowDetails || e.key !== 'ArrowDown') return;
              e.preventDefault();
              e.stopPropagation();
              askAboutChip(entry, e.currentTarget as HTMLElement);
            }}
            // A hold starts here and ends in the surface, so the card must go
            // now — otherwise it is still up, over the anchor, when the panel
            // lands on top of it.
            onPointerDown={closeHover}
            // A mouse only: a touch raises pointerenter too, and there the
            // sheet — not a card the finger is already covering — is the answer.
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') openHover(entry, e.currentTarget as HTMLElement); }}
            onPointerLeave={closeHover}
            onFocus={(e) => openHover(entry, e.currentTarget as HTMLElement)}
            onBlur={closeHover}
          >
            <Emoji aria-hidden="true">{entry.emoji}</Emoji>
            <Count aria-hidden="true">{entry.count}</Count>
          </ReactionChip>
        );
      })}
      {hover && (
        <ReactionHoverCard
          anchor={hover.anchor}
          bounds={hover.bounds}
          emoji={hover.emoji}
          names={hover.names}
          truncated={hover.truncated}
          lang={lang}
        />
      )}
    </ReactionChipsGroup>
  );
}

export default ReactionChips;
